/**
 * TimelineContainer - Container reutilizável para timelines do CRM
 * Garante consistência visual: ScrollArea com espaçamento correto e barra estilizada
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-10 - Componente criado para padronizar timelines
 */
import React from 'react';
import { Clock, LucideIcon } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

// Props do componente
interface TimelineContainerProps {
  children: React.ReactNode;
  /** Título do header (opcional - se não informado, não exibe header) */
  title?: string;
  /** Ícone customizado para o header (padrão: Clock) */
  icon?: LucideIcon;
  /** Exibe card com borda ao redor do conteúdo */
  showCard?: boolean;
  /** Classes adicionais para o ScrollArea */
  className?: string;
}

/**
 * Container reutilizável para timelines do CRM
 * Encapsula ScrollArea com espaçamento correto (pr-3) para a barra de rolagem
 * 
 * @example
 * // Uso básico (apenas scroll)
 * <TimelineContainer>
 *   <ActivityTimeline />
 * </TimelineContainer>
 * 
 * @example
 * // Com header e card
 * <TimelineContainer title="Histórico" showCard>
 *   <ActivityTimeline />
 * </TimelineContainer>
 */
export function TimelineContainer({ 
  children, 
  title, 
  icon: Icon = Clock,
  showCard = false,
  className 
}: TimelineContainerProps) {
  // Conteúdo com header opcional
  const content = (
    <>
      {title && (
        <div className="flex items-center gap-2 mb-3">
          <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center">
            <Icon className="h-3 w-3 text-muted-foreground" />
          </div>
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {title}
          </h3>
        </div>
      )}
      {children}
    </>
  );

  return (
    <ScrollArea className={cn("h-full pr-3", className)}>
      {showCard ? (
        <div className="rounded-lg border bg-card/50 p-3">
          {content}
        </div>
      ) : content}
    </ScrollArea>
  );
}
